/**
 * Verizon PIN encryption with Verizon's Voltage PIE. Horizon only accepts a Verizon account PIN encrypted
 * this way, so the browser encrypts it and the plain PIN never leaves it. Verizon hosts the two scripts:
 * `encryption.js` defines `ProtectString`, and `getkey.js` sets the current key on `PIE`. They are loaded
 * lazily, once the Verizon PIN step is reached, and kept for the page's lifetime once loaded.
 *
 * Nothing here logs, stores or returns the plain PIN, and no error carries it.
 */

const PIE_BASE_URLS = {
    production: "https://pie.prod.verizon.com",
    uat: "https://pie.uat.verizon.com",
} as const;

/** Loaded in order: `getkey.js` sets the key `ProtectString` encrypts with. */
const PIE_SCRIPT_PATHS = ["/pie/v1/encryption.js", "/pie/v1/verizon/getkey.js"] as const;

/** How long a script may take to load before verification gives up and sends the expert to the manual flow. */
export const PIE_LOAD_TIMEOUT_MS = 10_000;

/** Why the PIN could not be encrypted. Bounded values only, safe to log and report. */
export type VerizonPieFailure = "load-failed" | "load-timeout" | "not-loaded" | "missing-key" | "encrypt-failed";

export class VerizonPieUnavailableError extends Error {
    readonly failure: VerizonPieFailure;

    constructor(failure: VerizonPieFailure) {
        super(`Verizon PIN encryption unavailable: ${failure}`);
        this.name = "VerizonPieUnavailableError";
        this.failure = failure;
    }
}

/** The fields Horizon needs in place of the PIN. */
export interface EncryptedVerizonPin {
    encryptedPin: string;
    keyId: string;
    phaseBit: string;
}

interface PieGlobals {
    ProtectString?: (value: string) => string[] | undefined;
    PIE?: { key_id?: string; phase?: number | string };
}

const pieGlobals = () => globalThis as typeof globalThis & PieGlobals;

/** Verizon's production PIE in production, its UAT PIE everywhere else (matching Horizon's environments). */
export const getVerizonPieBaseUrl = (isProdMode: boolean): string =>
    isProdMode ? PIE_BASE_URLS.production : PIE_BASE_URLS.uat;

const loadScript = (src: string): Promise<void> =>
    new Promise((resolve, reject) => {
        const script = document.createElement("script");
        const fail = (failure: VerizonPieFailure) => {
            clearTimeout(timeout);
            script.remove();
            reject(new VerizonPieUnavailableError(failure));
        };
        const timeout = setTimeout(() => fail("load-timeout"), PIE_LOAD_TIMEOUT_MS);
        script.src = src;
        script.async = true;
        script.addEventListener(
            "load",
            () => {
                clearTimeout(timeout);
                resolve();
            },
            { once: true },
        );
        script.addEventListener("error", () => fail("load-failed"), { once: true });
        document.head.appendChild(script);
    });

/** The in-flight or completed load, so concurrent and later verifications share one set of scripts. */
let pieLoad: { baseUrl: string; promise: Promise<void> } | undefined;

const loadVerizonPie = (baseUrl: string): Promise<void> => {
    if (pieLoad?.baseUrl === baseUrl) return pieLoad.promise;
    const promise = (async () => {
        for (const path of PIE_SCRIPT_PATHS) await loadScript(`${baseUrl}${path}`);
    })();
    pieLoad = { baseUrl, promise };
    // A failed load is forgotten, so the expert's next attempt loads the scripts again.
    promise.catch(() => {
        if (pieLoad?.promise === promise) pieLoad = undefined;
    });
    return promise;
};

/**
 * Starts loading PIE when the PIN step renders, so the scripts are usually ready by the time the expert
 * submits. A failure is ignored here: `encryptVerizonPin` loads again and reports it.
 */
export const preloadVerizonPie = (isProdMode: boolean): void => {
    loadVerizonPie(getVerizonPieBaseUrl(isProdMode)).catch(() => undefined);
};

/**
 * Encrypts a Verizon account PIN for `/v1/verizon/verify-customer`, loading PIE first if needed.
 *
 * @throws {VerizonPieUnavailableError} when the scripts don't load or don't yield a key and ciphertext.
 */
export async function encryptVerizonPin(pin: string, isProdMode: boolean): Promise<EncryptedVerizonPin> {
    await loadVerizonPie(getVerizonPieBaseUrl(isProdMode));

    const { ProtectString, PIE } = pieGlobals();
    if (typeof ProtectString !== "function") throw new VerizonPieUnavailableError("not-loaded");

    let encryptedPin: string | undefined;
    try {
        encryptedPin = ProtectString(pin)?.[0];
    } catch {
        // Dropped on purpose: an error from the vendor script may echo its input.
        throw new VerizonPieUnavailableError("encrypt-failed");
    }

    const keyId = PIE?.key_id;
    const phase = PIE?.phase;
    if (!encryptedPin || !keyId || phase === undefined || phase === null || phase === "") {
        throw new VerizonPieUnavailableError("missing-key");
    }
    return { encryptedPin, keyId, phaseBit: String(phase) };
}

/** Test-only: forget the loaded scripts so each test starts from an unloaded page. */
export const resetVerizonPieForTests = () => {
    pieLoad = undefined;
};
